import { useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useMountEffect } from '../../hooks/useMountEffect'
import { checkCompletions } from '../../services/recommendations_api'

/** Poll every 2 minutes while the homepage is open. */
const POLL_INTERVAL_MS = 2 * 60 * 1000

interface UseCompletionPollingParams {
  /** The React Query key for the summary — invalidated when completions are found. */
  queryKey: unknown[]
  /**
   * Whether polling is active. Pass false when viewing another user's summary
   * (superuser mode) since checkCompletions always targets the authenticated user.
   */
  enabled: boolean
}

/**
 * Checks for CRM-side completions on mount and every 2 minutes while mounted.
 *
 * When the backend finds OPEN clicks that are now done (e.g. email replied,
 * deal stage changed), it marks them DONE with auto_completed=true and returns
 * { updated: N }. A non-zero N triggers a summary refetch so cards update to
 * "Manually Resolved" without requiring a full page reload.
 *
 * Uses a ref-based callback pattern so the interval always calls the latest
 * closure without needing to be torn down and re-created on each render.
 */
export function useCompletionPolling({
  queryKey,
  enabled,
}: UseCompletionPollingParams): void {
  const queryClient = useQueryClient()

  // Always up-to-date reference to avoid stale closures inside the interval.
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  const queryKeyRef = useRef(queryKey)
  queryKeyRef.current = queryKey

  useMountEffect(() => {
    const runCheck = async () => {
      if (!enabledRef.current) return
      try {
        const result = await checkCompletions()
        if (result?.updated && result.updated > 0) {
          queryClient.invalidateQueries({ queryKey: queryKeyRef.current })
        }
      } catch {
        // Best-effort — silently skip on network errors.
      }
    }

    runCheck()
    const id = setInterval(runCheck, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  })
}
